import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { beginProgramFetch } from "../../common-components/program-fetch-entry";
import DropdownIcons from "../../../../parts/inputs/dropdown-icons";
import { LOOM_URL } from "../../../../parts/modals/ClientConfigAnnouncementModal";
import SvgIcon from "../../../../parts/SvgIcon";
import TemplateBanner from "./TemplateBanner";

const HeaderTemplates = ({
  tabState,
  generateAIMealPlanAvailable,
  mealPlanAIApproved,
  query = {},
  onBack,
  bannerIcon,
  bannerTitle,
  bannerSubtitle,
  bannerGradientStart = "#00afe4",
  bannerGradientEnd = "#5bc500",
  bannerButton,
  count,
  onAddLeaderboard,
  referAsClients,
  coachId,
}) => {
  const { t } = useTranslation("coaching-portal");
  const planMode = query.planMode || "detailed";
  const status = query.status || "published";

  const sortItems = [
    {
      title: t("allTemplates.header.dateAdded"),
      src: "/img/svg/arrow-to-bottom.svg",
      alt: "createdDate",
      className: "icon-white",
      id: "createdDate",
    },
    {
      title: t("allTemplates.header.az"),
      src: "/img/svg/sort-amount-up.svg",
      alt: "name",
      className: "icon-white",
      id: "name",
    },
    {
      title: t("allTemplates.header.za"),
      src: "/img/svg/sort-amount-down.svg",
      alt: "nameDescending",
      className: "icon-white",
      id: "nameDescending",
    },
  ];

  const filteredSortItems =
    tabState === "check-in-tab" || tabState === "daily-accountability-tab"
      ? sortItems.filter((item) => item.id !== "createdDate")
      : sortItems;

  const TAB_TO_VIEW = {
    "programs-tab": "programs",
    "check-in-tab": "check-in",
    "daily-accountability-tab": "daily-accountability",
    "meal-plans-tab": "meal-plans",
    "teams-programs-tab": "teams-programs",
    "client-configuration-tab": "client-configuration",
    "leaderboard-tab": "leaderboard",
    "single-workouts-tab": "single-workouts",
  };
  const templateView = TAB_TO_VIEW[tabState] ?? "";
  const tabSuffix = `&tabView=${tabState}&templateView=${templateView}`;

  const urlSubPaths = {
    "programs-tab": (tabSuffix) =>
      `build-step-one&cycleType=new&isTemplate=true${tabSuffix}`,
    "check-in-tab": (tabSuffix) =>
      `add-questionnaire-template&section=questionnaire&fromEdit=true${tabSuffix}`,
    "daily-accountability-tab": (tabSuffix) =>
      `add-accountability-template&section=accountability&fromEdit=true${tabSuffix}`,
    "teams-programs-tab": (tabSuffix) =>
      `build-step-one&cycleType=new&isSubscription=true&storeType=teams&fromTemplate=true${tabSuffix}`,
    "single-workouts-tab": (tabSuffix) =>
      `build-step-one&cycleType=new&isTemplate=true&storeType=single-workout&fromTemplate=true${tabSuffix}`,
    "client-configuration-tab": (tabSuffix) =>
      `add-client-configuration-template${tabSuffix}`,
    "meal-plans-tab": (tabSuffix) => `handle-meal-plan${tabSuffix}`,
  };

  const AddButton = ({ classNames, text }) => {
    if (tabState === "leaderboard-tab") {
      return (
        <button
          type="button"
          className={classNames}
          data-button-type="primary"
          onClick={onAddLeaderboard}
        >
          <img
            src="/img/new-icons/add.svg"
            alt={t("allTemplates.header.addIconAlt")}
          />
          {text}
        </button>
      );
    }
    const urlBase = `/actions/refresh/?modal[type]=`;
    const finalUrl = `${urlBase}${urlSubPaths[tabState](tabSuffix)}`;
    return (
      <a
        className={classNames}
        role="button"
        data-button-type="primary"
        href={finalUrl}
      >
        <img
          src="/img/new-icons/add.svg"
          alt={t("allTemplates.header.addIconAlt")}
        />
        {text}
      </a>
    );
  };

  return (
    <header
      className={`header-coaching-programs${
        tabState === "meal-plans-tab" ? " meal-plans-detail-header" : ""
      }`}
    >
      <hgroup className="hgroup-templates">
        <div
          className={`header-text${
            tabState === "meal-plans-tab" ? "" : " w-full"
          }`}
        >
          <div className="flex items-center gap-4 mb-2">
            <SvgIcon
              src="/img/new-icons/back-button.svg"
              classNames={`cursor-pointer${
                tabState === "meal-plans-tab" ? " shrink-0" : ""
              }`}
              onClickFn={onBack}
            />
            {tabState === "meal-plans-tab" && (
              <h3 className="whitespace-nowrap">
                {planMode === "simplified"
                  ? t("allTemplates.chooseASection.singleDayMealPlans.title")
                  : t("allTemplates.chooseASection.multiDayMealPlans.title")}
              </h3>
            )}
            {tabState !== "meal-plans-tab" && (
              <TemplateBanner
                icon={bannerIcon}
                title={bannerTitle}
                subtitle={bannerSubtitle}
                gradientStart={bannerGradientStart}
                gradientEnd={bannerGradientEnd}
                action={bannerButton}
              />
            )}
          </div>
        </div>
      </hgroup>
      <nav role="tablist" className="border-none!">
        {tabState === "teams-programs-tab" && (
          <div className="toggle-options">
            <a
              role="button"
              data-button-type={
                status === "published" || status === undefined
                  ? "outline"
                  : "text-white"
              }
              href="/actions/refresh?status=published&tabView=teams-programs-tab&templateView=teams-programs"
            >
              <span>{t("allTemplates.header.published")}</span>
            </a>
            <a
              role="button"
              data-button-type={status === "draft" ? "outline" : "text-white"}
              href="/actions/refresh?status=draft&tabView=teams-programs-tab&templateView=teams-programs"
            >
              <span>{t("allTemplates.header.draft")}</span>
            </a>
          </div>
        )}
        {tabState !== "meal-plans-tab" &&
          tabState !== "teams-programs-tab" &&
          tabState !== "single-workouts-tab" &&
          !!count && (
            <div className="flex items-end ml-12">
              <span className="text-5xl font-light">
                {String(count).padStart(2, "0")}
              </span>
              <p className="ml-2 text-sm uppercase opacity-70">
                {count > 1
                  ? t("allTemplates.templateCard.templates")
                  : t("allTemplates.templateCard.template")}
              </p>
            </div>
          )}
        <hgroup>
          {tabState === "client-configuration-tab" && (
            // biome-ignore lint/a11y/useSemanticElements: external link styled as a button
            <a
              role="button"
              data-button-type="primary"
              href={LOOM_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("allTemplates.header.clientConfigTutorial")}
            </a>
          )}
          <DropdownIcons
            title={t("allTemplates.header.sort")}
            items={filteredSortItems}
            action={(sort) => {
              submit(`/actions/refresh`, {
                method: "GET",
                body: { sort },
              });
            }}
          />
          {tabState === "programs-tab" && (
            <button
              type="button"
              data-button-type="outline"
              onClick={() => {
                submit("/actions/refresh", {
                  method: "GET",
                  body: {
                    "modal[type]": "fetch",
                    owner: "coach",
                    fetchType: "template",
                    fetchSourceOwner: coachId,
                    // Empty strings are dropped by refresh-with, which clears
                    // a destination left behind by Copy Block From.
                    destinationCycleId: "",
                    destinationCycleName: "",
                    destinationCycleStatus: "created",
                    fetchEntry: beginProgramFetch(),
                    tabView: "programs-tab",
                    templateView: "programs",
                  },
                });
              }}
            >
              <span>
                {referAsClients
                  ? t("allTemplates.header.copyProgramFromAnotherClient")
                  : t("allTemplates.header.copyProgramFromAnotherAthlete")}
              </span>
            </button>
          )}
          <AddButton
            classNames="force-mobile-hidden"
            text={t("allTemplates.header.addNew")}
          />
          <AddButton
            classNames="force-only-show-mobile"
            text={t("allTemplates.header.addNew")}
          />
          {tabState === "meal-plans-tab" && generateAIMealPlanAvailable && (
            <a
              role="button"
              data-button-type="outline"
              className="generate-with-ai-button"
              href={`/actions/refresh/?modal[type]=${
                mealPlanAIApproved ? "generate-with-ai-plan" : "unlock-feature"
              }`}
            >
              <img
                src="/img/favicon/gemini.png"
                alt={t("allTemplates.header.geminiIconAlt")}
                style={{ width: "20px", height: "20px", marginRight: "6px" }}
              />
              {t("allTemplates.header.generateWithAI")}
            </a>
          )}
        </hgroup>
      </nav>
    </header>
  );
};

export default HeaderTemplates;
